import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PssActionType, PssAmmoDesign, PssConditionType, PssCrewMember, PssCharacterDesign, PssInventoryItem, PssItemDesign, PssRoom, PssRoomAction, PssRoomDesign } from '../../../services/pss-auth.service';
import { FeatureFlagService } from '../../../services/feature-flag.service';
import { AiEditorComponent, AiImportPayload } from '../ai-editor/ai-editor.component';
import { CrewFlyoutComponent } from '../crew-flyout/crew-flyout.component';
import { spriteIdOrEmpty } from '../pss-display-utils';
import { PssSpriteComponent } from '../../pss-sprite/pss-sprite.component';

@Component({
  selector: 'app-room-detail-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, AiEditorComponent, CrewFlyoutComponent, PssSpriteComponent],
  templateUrl: './room-detail-modal.component.html',
  styleUrls: ['../dashboard.component.css']
})
export class RoomDetailModalComponent {
  get canEditAi(): boolean { return this.featureFlags.isBasicPlus(); }

  constructor(private featureFlags: FeatureFlagService) {}

  @Input() room: PssRoom | null = null;
  @Input() roomDesigns = new Map<string, PssRoomDesign>();
  @Input() characterDesigns = new Map<string, PssCharacterDesign>();
  @Input() availableAmmo: PssAmmoDesign[] = [];
  @Input() selectedAmmoId: string | null = null;

  @Input() selectedRoomFireRate: number | null = null;
  @Input() selectedRoomMinDps: number | null = null;
  @Input() selectedRoomMaxDps: number | null = null;
  @Input() selectedRoomAdjacentAB = false;
  @Input() selectedRoomAbBoost = 0;
  @Input() selectedRoomStatLabel = 'Stat';
  @Input() selectedRoomCrewBoost = 0;
  @Input() selectedRoomCrew: PssCrewMember[] = [];
  @Input() selectedRoomStatKey: keyof PssCrewMember = 'weaponImp';
  @Input() itemDesigns = new Map<string, PssItemDesign>();
  @Input() inventory: PssInventoryItem[] = [];

  @Input() roomHasStatBoost = false;
  @Input() rules: PssRoomAction[] = [];
  @Input() conditionTypes: PssConditionType[] = [];
  @Input() actionTypes: PssActionType[] = [];
  /** What this room can load or build, in AI "Set Item {N}" order. */
  @Input() aiItems: PssAmmoDesign[] = [];
  @Input() roomAiSaving = false;
  @Input() roomAiError = '';
  @Input() userId = '';
  @Input() allianceId = '';
  @Input() userName = '';
  @Input() initialConditionId = '1';
  @Input() initialActionId = '1';

  @Output() close = new EventEmitter<void>();
  @Output() selectedAmmoIdChange = new EventEmitter<string | null>();
  @Output() addRule = new EventEmitter<{ conditionTypeId: string; actionTypeId: string }>();
  @Output() deleteRule = new EventEmitter<PssRoomAction>();
  @Output() moveRule = new EventEmitter<{ rule: PssRoomAction; newIndex: number }>();
  @Output() selectCrew = new EventEmitter<PssCrewMember>();
  @Output() importRules = new EventEmitter<AiImportPayload>();
  @Output() deleteAllRules = new EventEmitter<void>();
  @Output() editRule = new EventEmitter<{ rule: any; conditionTypeId: string; actionTypeId: string }>();

  roomType(roomDesignId: string): string {
    return this.roomDesigns.get(roomDesignId)?.roomType ?? '';
  }

  roomName(roomDesignId: string): string {
    return this.roomDesigns.get(roomDesignId)?.name ?? `Room#${roomDesignId}`;
  }

  roomSpriteId(roomDesignId: string): string {
    return spriteIdOrEmpty(this.roomDesigns.get(roomDesignId)?.imageSpriteId);
  }

  crewName(c: PssCrewMember): string {
    return c.customName || this.characterDesigns.get(c.designId)?.name || `#${c.designId}`;
  }

  roomStatFmt(n: number | null): string {
    if (n === null || !isFinite(n) || isNaN(n)) return '—';
    return n.toFixed(2);
  }

  crewSpriteId(designId: string): string {
    return spriteIdOrEmpty(this.characterDesigns.get(designId)?.profileSpriteId);
  }

  selectedCrewFlyout: PssCrewMember | null = null;

  openCrewFlyout(c: PssCrewMember): void {
    this.selectedCrewFlyout = this.selectedCrewFlyout === c ? null : c;
  }

  private static readonly CREW_STAT_KEYS: Array<keyof PssCrewMember> = [
    'weaponImp', 'attackImp', 'scienceImp', 'engineImp', 'pilotImp', 'repairImp', 'abilityImp', 'hpImp',
  ];

  private static readonly CREW_STAT_KEY_TO_ITEM_TYPE: Partial<Record<keyof PssCrewMember, string>> = {
    weaponImp: 'Weapon',
    attackImp: 'Attack',
    scienceImp: 'Science',
    engineImp: 'Engine',
    pilotImp: 'Pilot',
    repairImp: 'Repair',
    abilityImp: 'Ability',
    hpImp: 'HP',
  };

  private static readonly CREW_STAT_KEY_TO_CHARACTER_FIELD: Partial<Record<keyof PssCrewMember, keyof PssCharacterDesign>> = {
    weaponImp: 'finalWeapon',
    attackImp: 'finalAttack',
    scienceImp: 'finalScience',
    engineImp: 'finalEngine',
    pilotImp: 'finalPilot',
    repairImp: 'finalRepair',
    abilityImp: 'specialAbilityArg',
    hpImp: 'finalHp',
  };

  private parseItemBonusValue(raw: string | undefined): number {
    if (!raw) return 0;
    const match = raw.trim().match(/[-+]?\d+(?:\.\d+)?/);
    if (!match) return 0;
    const parsed = Number(match[0]);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private getCrewGearBoost(c: PssCrewMember, key: keyof PssCrewMember): number {
    const requiredType = RoomDetailModalComponent.CREW_STAT_KEY_TO_ITEM_TYPE[key];
    if (!requiredType) return 0;

    let total = 0;
    const requiredLower = requiredType.toLowerCase();

    const addDesignBonuses = (item: PssItemDesign | undefined): number => {
      if (!item) return 0;
      let value = 0;
      if ((item.enhancementType || '').trim().toLowerCase() === requiredLower) {
        value += this.parseItemBonusValue(item.enhancementValue || item.modifier);
      }
      if ((item.enhancementType2 || '').trim().toLowerCase() === requiredLower) {
        value += this.parseItemBonusValue(item.enhancementValue2 || item.modifier2);
      }
      if ((item.bonusEnhancementType || '').trim().toLowerCase() === requiredLower) {
        value += this.parseItemBonusValue(item.bonusEnhancementValue || '');
      }
      if ((item.bonusEnhancementType2 || '').trim().toLowerCase() === requiredLower) {
        value += this.parseItemBonusValue(item.bonusEnhancementValue2 || '');
      }
      return value;
    };

    if (c.equipItemIds && c.equipItemIds.length > 0 && this.inventory.length > 0) {
      const itemIdSet = new Set(c.equipItemIds);
      for (const instanceId of itemIdSet) {
        const invItem = this.inventory.find(inv => inv.equippedItemIds?.includes(instanceId));
        if (!invItem) continue;
        total += addDesignBonuses(this.itemDesigns.get(invItem.designId));

        for (const bonus of invItem.instanceBonuses?.filter(b => b.itemId === instanceId) ?? []) {
          if ((bonus.enhancementType || '').trim().toLowerCase() === requiredLower) {
            total += this.parseItemBonusValue(bonus.enhancementValue || '');
          }
          if ((bonus.enhancementType2 || '').trim().toLowerCase() === requiredLower) {
            total += this.parseItemBonusValue(bonus.enhancementValue2 || '');
          }
        }
      }
      return total;
    }

    if (c.equipDesignIds && c.equipDesignIds.length > 0) {
      for (const equipId of c.equipDesignIds) {
        const item = this.itemDesigns.get(equipId);
        total += addDesignBonuses(item);
      }
    }

    return total;
  }

  crewBaseStat(c: PssCrewMember): number {
    const key = this.selectedRoomStatKey;
    const charDesign = this.characterDesigns.get(c.designId);
    const statField = RoomDetailModalComponent.CREW_STAT_KEY_TO_CHARACTER_FIELD[key];
    const base = charDesign && statField ? parseFloat((charDesign[statField] as string) || '0') : 0;
    return Math.max(0, base);
  }

  crewTrainingPct(c: PssCrewMember): number {
    const key = this.selectedRoomStatKey;
    return parseInt((c[key] as string) || '0', 10) || 0;
  }

  crewGearBoost(c: PssCrewMember): number {
    return this.getCrewGearBoost(c, this.selectedRoomStatKey);
  }

  crewEffectiveStat(c: PssCrewMember): number {
    const base = this.crewBaseStat(c);
    const gear = this.crewGearBoost(c);
    const training = this.crewTrainingPct(c);

    const baseWithTraining = base * (1 + training / 100); // base * (1 + training%)

    if (this.selectedRoomStatKey === 'abilityImp') {
      const abilityTotal = baseWithTraining * (1 + gear / 100);
      return Number.isFinite(abilityTotal) ? abilityTotal : 0;
    }

    const statTotal = baseWithTraining + gear;
    return Number.isFinite(statTotal) ? statTotal : 0;
  }

  crewStat(c: PssCrewMember): string {
    const value = this.crewEffectiveStat(c);
    if (value > 0) return value.toFixed(1);

    // Fallback to any other training stat if nothing matches.
    for (const fallbackKey of RoomDetailModalComponent.CREW_STAT_KEYS) {
      const fallback = parseInt((c[fallbackKey] as string) || '0', 10) || 0;
      if (fallback > 0) return String(fallback);
    }
    return '0';
  }
}
